import React, { useEffect, useRef, useState } from "react";
import { UserPackageBadge } from "../../components/userPackageBadge/userPackageBadge";
import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import { ModalBody } from "../../components/modalCommon/modalBody";
import type { AddDictionaryModalModel } from "../../type/dictionary";

const AddDictionaryModal: React.FC<AddDictionaryModalModel> = ({
  show,
  onHide,
  onSave,
  canAddDictionary,
  remainingDictionaries,
  maxDictionaries,
  packageName,
}) => {
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!show) {
      setName("");
      setIsSubmitting(false);
    }
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !canAddDictionary) return;

    setIsSubmitting(true);
    try {
      await onSave(name.trim());

      // prepare for quickly adding another dictionary
      setName("");
      nameInputRef.current?.focus();
    } catch (err) {
      console.error("Error saving dictionary:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal open={show} onClose={onHide} size="md">
      <ModalHeader title="Add New Dictionary" onClose={onHide} />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Dictionary Name (30 characters max)</label>
            <input
              ref={nameInputRef}
              type="text"
              className="form-control bg-dark text-light border-secondary"
              placeholder="Enter dictionary name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canAddDictionary || isSubmitting}
              autoFocus
              maxLength={30}
            />
          </div>

          {maxDictionaries !== -1 && (
            <div className="d-flex align-items-center justify-content-center mb-3">
              <UserPackageBadge packageName={packageName} />
              <span
                className={`ms-3 ${
                  remainingDictionaries === 0
                    ? "text-danger"
                    : "text-light-green"
                }`}
              >
                {remainingDictionaries} out of {maxDictionaries} items left
              </span>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary w-50"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Close
          </button>

          <button
            type="submit"
            className="btn btn-success w-50"
            disabled={isSubmitting || !name.trim() || !canAddDictionary}
          >
            {isSubmitting ? "Creating…" : "+ Add"}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default AddDictionaryModal;